import { ConfirmDialog, Field, TextInput, useToast } from "@nabble/ui";
import { useState } from "react";
import { anonymizeUser, type AdminUser } from "../../../api/adminUsers";
import { errorMessage } from "../../../errors";

interface AnonymizeUserDialogProps {
  user: AdminUser;
  onClose: () => void;
  onAnonymized: () => void;
}

/** Deletes a user's personal data (Law 21.719) once the administrator types their email to confirm. */
export function AnonymizeUserDialog({ user, onClose, onAnonymized }: AnonymizeUserDialogProps): React.JSX.Element {
  const { show } = useToast();
  const [confirmation, setConfirmation] = useState("");
  const matches = confirmation.trim().toLowerCase() === user.email.toLowerCase();

  async function anonymize(): Promise<void> {
    try {
      await anonymizeUser(user.id);
      show({ title: "User anonymized", text: "Their personal data was deleted.", tone: "success" });
      onAnonymized();
    } catch (err) {
      show({ title: "We couldn't anonymize the user", text: errorMessage(err, "Please try again."), tone: "danger" });
    }
  }

  return (
    <ConfirmDialog
      open
      tone="danger"
      icon="userX"
      title="Anonymize user"
      description="This can't be undone."
      confirmLabel="Anonymize"
      confirmDisabled={!matches}
      onConfirm={anonymize}
      onClose={onClose}
    >
      <p>
        The personal data of <strong>{user.email}</strong> will be deleted under Chilean Law 21.719. The audit trail is kept, without their
        identity.
      </p>
      <Field label="Type the email to confirm">
        <TextInput value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off" spellCheck={false} placeholder={user.email} />
      </Field>
    </ConfirmDialog>
  );
}
